// src/hooks/useUserPreferences.js - COMPLETE FIXED VERSION
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import userPreferencesService from '../services/userPreferences';
import { toast } from '../components/Toast';

// No API base here on purpose: every request in this hook goes through
// userPreferencesService, which owns the base. The module-level constant
// that used to sit here was never read — a seventh copy of the resolution
// that only looked load-bearing.

function deepEqual(a, b) {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

// Enhanced debounce helper that returns both debounced function and flush method
function useDebounceWithFlush(func, delay) {
  const timeoutRef = useRef(null);
  const callbackRef = useRef(func);
  
  // Update the callback ref when func changes
  useEffect(() => {
    callbackRef.current = func;
  }, [func]);
  
  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (callbackRef.current) {
      callbackRef.current();
    }
  }, []);
  
  const debounced = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      if (callbackRef.current) {
        callbackRef.current(...args);
      }
    }, delay);
  }, [delay]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return { debounced, flush };
}

export function useUserPreferences() {
  const { user, loading: authLoading } = useAuth();
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const service = userPreferencesService;
  const updateQueueRef = useRef(new Map());
  const isMountedRef = useRef(true);
  const migratedRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const isUpdatingRef = useRef(false); // Track update state in ref too
  const failureNotifiedRef = useRef(false); // One save-failure toast per streak

  const loadFromLocalStorage = useCallback(() => {
    const localPrefs = {};
    try {
      const pinnedStr = localStorage.getItem('pinnedDepartments');
      if (pinnedStr) {
        const ids = JSON.parse(pinnedStr);
        if (Array.isArray(ids) && ids.length > 0) {
          localPrefs.pinned_departments = { department_ids: ids };
        }
      }
    } catch (e) {
      console.warn('Error parsing pinnedDepartments:', e);
    }
    
    try {
      const orderStr = localStorage.getItem('departmentOrder');
      if (orderStr) {
        const order = JSON.parse(orderStr);
        if (Array.isArray(order) && order.length > 0) {
          localPrefs.department_order = { order };
        }
      }
    } catch (e) {
      console.warn('Error parsing departmentOrder:', e);
    }
    
    return localPrefs;
  }, []);

  const migrateFromLocalStorage = useCallback(async (existingPrefs) => {
    if (!user || !isMountedRef.current || migratedRef.current) return;

    const localPrefs = loadFromLocalStorage();
    const migrations = [];
    
    if (!existingPrefs?.pinned_departments?.department_ids?.length && 
        localPrefs.pinned_departments?.department_ids?.length) {
      migrations.push({ 
        type: 'pinned_departments', 
        value: localPrefs.pinned_departments 
      });
    }
    
    if (!existingPrefs?.department_order?.order?.length && 
        localPrefs.department_order?.order?.length) {
      migrations.push({ 
        type: 'department_order', 
        value: localPrefs.department_order 
      });
    }

    if (migrations.length > 0) {
      console.log('Migrating', migrations.length, 'preferences from localStorage');
      
      // Update state immediately with migrated data
      setPreferences(prev => {
        const updated = { ...prev };
        migrations.forEach(m => {
          updated[m.type] = m.value;
        });
        return updated;
      });
      
      // Save to backend
      for (const m of migrations) {
        try {
          await service.updatePreference(m.type, m.value);
          console.log('Migrated', m.type, 'to backend');
        } catch (err) {
          console.error('Failed to migrate', m.type, ':', err);
        }
      }
      
      // Clear localStorage after successful migration
      localStorage.removeItem('pinnedDepartments');
      localStorage.removeItem('departmentOrder');
      migratedRef.current = true;
    }
  }, [user, service, loadFromLocalStorage]);

  const loadPreferences = useCallback(async () => {
    // Don't load if:
    // 1. Auth is still loading
    // 2. Component is unmounted
    // 3. Currently updating (prevents race condition)
    if (authLoading || !isMountedRef.current || isUpdatingRef.current) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (user) {
        const data = await service.fetchAllPreferences();
        
        if (!isMountedRef.current) return;
        
        const isValidData = data && typeof data === 'object';
        
        if (isValidData) {
          // Only update state if data is different (prevents unnecessary re-renders)
          setPreferences(prev => {
            if (deepEqual(prev, data)) return prev;
            return data;
          });
          hasLoadedRef.current = true;
          
          // Migrate any local data if needed
          await migrateFromLocalStorage(data);
        } else {
          throw new Error('Invalid preferences data from server');
        }
      } else {
        // No user - use localStorage
        const localPrefs = loadFromLocalStorage();
        setPreferences(localPrefs);
      }
    } catch (err) {
      const isAuthError = err.code === 'NO_AUTH' || err.code === 'AUTH_EXPIRED';
      const isNetworkError = err.name === 'TypeError' || err.code === 'SERVER_ERROR';
      
      console.error('Error loading preferences:', err);
      
      if (isMountedRef.current) {
        setError(err.message);
        
        if (isAuthError) {
          console.warn('Authentication issue - user may need to re-login');
          if (!hasLoadedRef.current) {
            const localPrefs = loadFromLocalStorage();
            if (Object.keys(localPrefs).length > 0) {
              setPreferences(localPrefs);
            }
          }
        } else if (isNetworkError && !hasLoadedRef.current) {
          const localPrefs = loadFromLocalStorage();
          setPreferences(localPrefs);
        }
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [user, authLoading, service, loadFromLocalStorage, migrateFromLocalStorage]);

  // Store loadPreferences in a ref to avoid dependency issues
  const loadPreferencesRef = useRef(loadPreferences);
  useEffect(() => {
    loadPreferencesRef.current = loadPreferences;
  }, [loadPreferences]);

  // Load preferences when component mounts and auth is ready
  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;

    // Only do initial load once when auth is ready
    if (!authLoading && !initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      loadPreferencesRef.current();
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [authLoading]);

  // Reload when user ID changes (login/logout) - NOT when loadPreferences reference changes
  const prevUserIdRef = useRef(user?.id);
  useEffect(() => {
    // Only run if user ID actually changed (login/logout), not on every render
    if (prevUserIdRef.current !== user?.id) {
      prevUserIdRef.current = user?.id;

      if (!authLoading && isMountedRef.current && !isUpdatingRef.current) {
        service.clearCache();
        hasLoadedRef.current = false;
        migratedRef.current = false;
        loadPreferencesRef.current();
      }
    }
  }, [user?.id, authLoading, service]);

  // Backend update function
  const performBackendUpdate = useCallback(async () => {
    if (!user || !isMountedRef.current || updateQueueRef.current.size === 0) return;
    
    setIsUpdating(true);
    isUpdatingRef.current = true;
    
    const updates = Array.from(updateQueueRef.current.entries());
    updateQueueRef.current.clear();
    
    console.log('Saving', updates.length, 'preference updates to backend');
    
    let failed = false;
    for (const [type, value] of updates) {
      try {
        // CRITICAL: Make sure value is not undefined
        if (value === undefined || value === null) {
          console.error(`Cannot update ${type}: value is undefined/null`);
          continue;
        }

        console.log(`Updating ${type} with value:`, value);
        await service.updatePreference(type, value);
      } catch (err) {
        console.error(`Failed to update ${type}:`, err.message);
        // Re-queue failed update
        updateQueueRef.current.set(type, value);
        failed = true;
      }
    }

    // The re-queue used to be invisible — pins/order could be lost with no
    // signal (slice 4). One toast per failure streak, not per retry.
    if (failed && !failureNotifiedRef.current) {
      failureNotifiedRef.current = true;
      toast('Couldn’t save your preferences — will keep retrying', 'error');
    } else if (!failed && updates.length > 0) {
      failureNotifiedRef.current = false;
    }

    setIsUpdating(false);
    isUpdatingRef.current = false;
  }, [user, service]);

  // Use debounce with flush capability
  const { debounced: debouncedUpdate, flush: flushUpdates } = useDebounceWithFlush(performBackendUpdate, 2000);

  const updatePreference = useCallback((type, value) => {
    console.log('Updating preference:', type, value);
    
    // CRITICAL: Validate value before proceeding
    if (value === undefined || value === null) {
      console.error('❌ Cannot update preference with missing value:', {
        type,
        value,
        stack: new Error().stack
      });
      return Promise.reject(new Error('Missing value'));
    }
    
    // Update state optimistically (immediately)
    setPreferences(prev => {
      // Use functional update to avoid stale closures
      const newPrefs = { ...prev, [type]: value };
      
      // Only update if actually different
      if (deepEqual(prev[type], value)) {
        console.log('No change needed for', type);
        return prev;
      }
      
      console.log('State updated for', type);
      return newPrefs;
    });

    // Always save to localStorage as backup (immediate)
    if (type === 'pinned_departments') {
      const ids = value?.department_ids || [];
      localStorage.setItem('pinnedDepartments', JSON.stringify(ids));
      console.log('Saved to localStorage:', ids);
    } else if (type === 'department_order') {
      const order = value?.order || [];
      localStorage.setItem('departmentOrder', JSON.stringify(order));
      console.log('Saved order to localStorage:', order);
    }

    // Queue backend update if user is logged in
    if (user) {
      updateQueueRef.current.set(type, value);
      debouncedUpdate();
    }
    
    return Promise.resolve();
  }, [user, debouncedUpdate]);

  // Flush pending updates before unmount or when critical
  useEffect(() => {
    return () => {
      // Flush any pending updates when component unmounts
      if (updateQueueRef.current.size > 0) {
        console.log('Flushing pending updates before unmount');
        flushUpdates();
      }
    };
  }, [flushUpdates]);

  const getPreference = useCallback((type, defaultValue = null) => {
    return preferences[type] || defaultValue;
  }, [preferences]);

  // Derived values with proper defaults
  const pinnedDepartments = (getPreference('pinned_departments', { department_ids: [] })?.department_ids || []).map(String);
  const departmentOrder = (getPreference('department_order', { order: [] })?.order || []).map(String);
  const uiSettings = getPreference('ui_settings', {});

  const setPinnedDepartments = useCallback((departmentIds) => {
    console.log('setPinnedDepartments called with:', departmentIds);
    
    // Ensure we have an array
    const ids = Array.from(new Set((departmentIds || []).map(String)));
    console.log('  Processed IDs:', ids);
    
    // Create the value object
    const valueObject = { department_ids: ids };
    console.log('  Value object:', valueObject);
    
    // Make sure the value object is valid
    if (!valueObject || typeof valueObject !== 'object') {
      console.error('Invalid value object:', valueObject);
      return Promise.reject(new Error('Invalid value object'));
    }
    
    return updatePreference('pinned_departments', valueObject);
  }, [updatePreference]);

  const setDepartmentOrder = useCallback((order) => {
    console.log('setDepartmentOrder called with:', order);
    
    // Ensure we have an array
    const ids = Array.from(new Set((order || []).map(String)));
    console.log('  Processed order:', ids);
    
    // Create the value object
    const valueObject = { order: ids };
    console.log('  Value object:', valueObject);
    
    // Make sure the value object is valid
    if (!valueObject || typeof valueObject !== 'object') {
      console.error('Invalid value object:', valueObject);
      return Promise.reject(new Error('Invalid value object'));
    }
    
    return updatePreference('department_order', valueObject);
  }, [updatePreference]);

  const setUiSettings = useCallback((settings) => {
    const next = { ...uiSettings, ...settings };
    if (deepEqual(next, uiSettings)) return;
    return updatePreference('ui_settings', next);
  }, [updatePreference, uiSettings]);

  // Return the hook values
  return {
    preferences,
    loading,
    error,
    isUpdating,
    updatePreference,
    getPreference,
    reload: loadPreferences,
    pinnedDepartments,
    departmentOrder,
    uiSettings,
    setPinnedDepartments,
    setDepartmentOrder,
    setUiSettings
  };
}

// Convenience hook for pinned departments
export function usePinnedDepartments() {
  const {
    pinnedDepartments: departments,
    departmentOrder: order,
    setPinnedDepartments: updatePinned,
    setDepartmentOrder: updateOrder,
    loading,
    error,
    isUpdating
  } = useUserPreferences();

  const arraysEqual = (a = [], b = []) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

  const setPinnedDepartments = useCallback(async (newPins) => {
    console.log('usePinnedDepartments.setPinnedDepartments called with:', newPins);
    
    // Handle functional updates
    const pinsToSet = typeof newPins === 'function' ? newPins(departments) : newPins;
    
    // Validate the pins
    if (!Array.isArray(pinsToSet)) {
      console.error('Invalid pins provided:', pinsToSet);
      return departments; // Return current state if invalid
    }
    
    const ids = Array.from(new Set(pinsToSet.filter(id => id != null).map(String)));
    
    // Skip if no change
    if (arraysEqual(ids, departments)) {
      console.log('No change in pins, skipping update');
      return departments;
    }
    
    console.log('Setting pins from', departments, 'to', ids);
    
    try {
      await updatePinned(ids);
      return ids;
    } catch (error) {
      console.error('Failed to update pins:', error);
      return departments; // Return current state on error
    }
  }, [departments, updatePinned]);

  const setDepartmentOrder = useCallback(async (newOrder) => {
    console.log('usePinnedDepartments.setDepartmentOrder called with:', newOrder);
    
    // Handle functional updates
    const orderToSet = typeof newOrder === 'function' ? newOrder(order) : newOrder;
    
    // Validate the order
    if (!Array.isArray(orderToSet)) {
      console.error('Invalid order provided:', orderToSet);
      return order; // Return current state if invalid
    }
    
    const ids = Array.from(new Set(orderToSet.filter(id => id != null).map(String)));
    
    // Skip if no change
    if (arraysEqual(ids, order)) {
      console.log('No change in order, skipping update');
      return order;
    }
    
    console.log('Setting order from', order, 'to', ids);
    
    try {
      await updateOrder(ids);
      return ids;
    } catch (error) {
      console.error('Failed to update order:', error);
      return order; // Return current state on error
    }
  }, [order, updateOrder]);

  return {
    pinnedDepartments: departments,
    setPinnedDepartments,
    departmentOrder: order,
    setDepartmentOrder,
    loading,
    error,
    isUpdating
  };
}